import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { SEO, StructuredData } from '@/components/seo'

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-surface-950 text-white">
      <SEO
        title="Terms of Service"
        description="JumpstoneHR Terms of Service. Review the terms and conditions governing your use of our AI-powered hiring decision platform."
        canonical="/terms"
        structuredData={[
          StructuredData.webPage(
            'Terms of Service',
            'JumpstoneHR terms of service and conditions of use.',
            '/terms'
          ),
          StructuredData.breadcrumb([
            { name: 'Home', url: '/' },
            { name: 'Terms of Service', url: '/terms' },
          ]),
        ]}
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-16 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <defs>
                <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                  <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>
          
          <div className="relative mx-auto max-w-4xl section-padding">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px flex-1 max-w-[60px] bg-brand-500" />
              <span className="text-brand-400 text-sm font-medium tracking-widest uppercase">Legal</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-6">
              Terms of Service
            </h1>
            
            <p className="text-surface-400">
              Last updated: January 1, 2026
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="pb-24">
          <div className="mx-auto max-w-4xl section-padding">
            <div className="prose prose-invert prose-lg max-w-none">
              <div className="space-y-12">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Agreement to Terms</h2>
                  <p className="text-surface-300 leading-relaxed">
                    These Terms of Service ("Terms") constitute a legally binding agreement between you and Jumpstone Technology Inc., an Ontario corporation ("Jumpstone", "we", "us", or "our"). By accessing or using JumpstoneHR (the "Service"), you agree to be bound by these Terms. If you do not agree to these Terms, do not use the Service.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Description of Service</h2>
                  <p className="text-surface-300 leading-relaxed">
                    JumpstoneHR is an AI-powered hiring decision platform that helps organizations evaluate candidates, manage hiring workflows, and make data-driven hiring decisions. The Service includes candidate management, AI-assisted analysis, interview coordination, and decision support tools.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Account Registration</h2>
                  <p className="text-surface-300 leading-relaxed mb-4">To use the Service, you must:</p>
                  <ul className="list-disc list-inside text-surface-300 space-y-2 ml-4">
                    <li>Be at least 18 years old</li>
                    <li>Provide accurate and complete registration information</li>
                    <li>Maintain the security of your account credentials</li>
                    <li>Promptly notify us of any unauthorized access to your account</li>
                    <li>Accept responsibility for all activities under your account</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Acceptable Use</h2>
                  <p className="text-surface-300 leading-relaxed mb-4">You agree not to:</p>
                  <ul className="list-disc list-inside text-surface-300 space-y-2 ml-4">
                    <li>Use the Service for any unlawful purpose or in violation of applicable laws</li>
                    <li>Upload malicious code or attempt to compromise our systems</li>
                    <li>Collect or store personal data of candidates without proper authorization</li>
                    <li>Use the Service to discriminate against candidates based on protected characteristics</li>
                    <li>Reverse engineer, decompile, or disassemble the Service</li>
                    <li>Resell or redistribute the Service without our written consent</li>
                    <li>Use automated systems to access the Service beyond normal usage patterns</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Your Data and Content</h2>
                  <p className="text-surface-300 leading-relaxed mb-4">
                    You retain ownership of all data and content you upload to the Service ("Your Content"). By using the Service, you grant us a limited license to:
                  </p>
                  <ul className="list-disc list-inside text-surface-300 space-y-2 ml-4">
                    <li>Store, process, and display Your Content to provide the Service</li>
                    <li>Use AI to analyze Your Content and generate hiring insights</li>
                    <li>Create anonymized and aggregated data for service improvement</li>
                  </ul>
                  <p className="text-surface-300 leading-relaxed mt-4">
                    You represent that you have all necessary rights and consents to upload content, including candidate data, and that such use complies with applicable privacy and employment laws.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">AI-Generated Recommendations</h2>
                  <p className="text-surface-300 leading-relaxed mb-4">
                    The Service uses artificial intelligence to provide hiring recommendations. You acknowledge and agree that:
                  </p>
                  <ul className="list-disc list-inside text-surface-300 space-y-2 ml-4">
                    <li>AI recommendations are advisory only and do not constitute hiring decisions</li>
                    <li>You are solely responsible for all hiring decisions made using our platform</li>
                    <li>AI systems may have limitations and potential biases</li>
                    <li>You should review and validate AI recommendations before acting on them</li>
                    <li>We do not guarantee any specific hiring outcomes</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Fees and Payment</h2>
                  <p className="text-surface-300 leading-relaxed mb-4">
                    Subscription fees are charged in advance on a monthly or annual basis. You agree to:
                  </p>
                  <ul className="list-disc list-inside text-surface-300 space-y-2 ml-4">
                    <li>Pay all applicable fees according to your selected plan</li>
                    <li>Provide accurate billing information and keep it updated</li>
                    <li>Authorize us to charge your payment method for recurring fees</li>
                  </ul>
                  <p className="text-surface-300 leading-relaxed mt-4">
                    All fees are quoted and payable in Canadian dollars (CAD) unless otherwise specified. Fees are non-refundable except as expressly stated in these Terms or required by law.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Term and Termination</h2>
                  <p className="text-surface-300 leading-relaxed mb-4">
                    These Terms commence when you create an account and continue until terminated. You may terminate by canceling your subscription and closing your account. We may terminate or suspend your access if:
                  </p>
                  <ul className="list-disc list-inside text-surface-300 space-y-2 ml-4">
                    <li>You breach these Terms</li>
                    <li>You fail to pay applicable fees</li>
                    <li>We are required to do so by law</li>
                    <li>We discontinue the Service</li>
                  </ul>
                  <p className="text-surface-300 leading-relaxed mt-4">
                    Upon termination, your right to use the Service ends immediately. We will retain your data for a reasonable period to allow you to export it, after which it will be deleted.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Intellectual Property</h2>
                  <p className="text-surface-300 leading-relaxed">
                    The Service, including its design, features, code, and documentation, is owned by Jumpstone Technology Inc. and protected by intellectual property laws. Nothing in these Terms grants you any right to use our trademarks, logos, or brand features without prior written consent.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Disclaimer of Warranties</h2>
                  <p className="text-surface-300 leading-relaxed">
                    THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Limitation of Liability</h2>
                  <p className="text-surface-300 leading-relaxed">
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, JUMPSTONE TECHNOLOGY INC. SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL, ARISING FROM YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY SHALL NOT EXCEED THE FEES PAID BY YOU IN THE TWELVE MONTHS PRECEDING THE CLAIM.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Indemnification</h2>
                  <p className="text-surface-300 leading-relaxed">
                    You agree to indemnify and hold harmless Jumpstone Technology Inc. and its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including legal fees) arising from your use of the Service, violation of these Terms, or infringement of any third-party rights.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Governing Law</h2>
                  <p className="text-surface-300 leading-relaxed">
                    These Terms are governed by the laws of the Province of Ontario and the federal laws of Canada applicable therein, without regard to conflict of law principles. Any disputes arising from these Terms shall be resolved exclusively in the courts of Ontario, and you consent to the personal jurisdiction of such courts.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Changes to Terms</h2>
                  <p className="text-surface-300 leading-relaxed">
                    We may modify these Terms at any time by posting the revised terms on our website. Material changes will be communicated via email or through the Service. Your continued use after such changes constitutes acceptance of the new Terms.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Contact Information</h2>
                  <p className="text-surface-300 leading-relaxed mb-4">
                    For questions about these Terms, please contact us:
                  </p>
                  <div className="bg-surface-900 border border-surface-800 rounded-xl p-6">
                    <p className="text-white font-semibold">Jumpstone Technology Inc.</p>
                    <p className="text-surface-400 mt-2">Email: legal@jumpstonehr.com</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  )
}

